import { useSnackbar } from 'notistack';
import { useApi } from '../../../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import {
	CerebroForm,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
	CerebroTextarea,
} from '../../../shared/components';
import { Field, Formik, FormikErrors } from 'formik';
import { FormikUtils, NumberUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { QueryKeys } from '../../../shared/hooks';
import {
	TermSheetFacility,
	TermSheetFacilityFee,
	TermSheetFeeFrequencies,
	TermSheetFeeFrequenciesMap,
	TermSheetFeeTypes,
} from '../../../shared/models';
import { getApiResponseData } from '../../../shared/utils/axios';

interface NotesFormDialogProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	facility: TermSheetFacility | null;
	fee: TermSheetFacilityFee | null;
	close: () => void;
}

export const NotesFormDialog: React.FC<NotesFormDialogProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	facility,
	fee,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const notesMutation = useMutation(
		async ({ values }: NotesFormMutationVariables) => {
			const patchValues = {
				...fee,
				feeNotes: values.feeNotes,
			};

			const { data, error } = await getApiResponseData<TermSheetFacilityFee>(
				api.patch(
					CerebroApiEndpoints.TermSheets.SpecificFacilityFee(
						dataRoomGuid,
						termSheetGuid,
						revisionGuid,
						facility?.guid,
						fee?.guid
					),
					patchValues
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(
					QueryKeys.TermSheets.FacilityFees(dataRoomGuid, termSheetGuid, revisionGuid, facility?.guid)
				);
				client.invalidateQueries(QueryKeys.TermSheets.Specific(dataRoomGuid, termSheetGuid));
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
				snackbar.enqueueSnackbar('Facility Notes was successfully saved.', {
					variant: 'success',
				});
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
				close();
			},
		}
	);

	return (
		<Dialog
			open={fee !== null}
			onClose={close}
			sx={{
				'& .MuiDialog-paper': {
					width: '62%',
					maxWidth: 'none',
					height: '70%',
					maxHeight: 'none',
				},
			}}
		>
			<DialogTitle>Summary / Notes</DialogTitle>
			<DialogContent>
				<Formik
					initialValues={{
						termSheetFeeGuid: fee?.termSheetFee.guid,
						feeAmount:
							(fee?.termSheetFeeType ?? 'Currency') === 'Currency'
								? NumberUtils.FormatDecimal(fee?.feeAmount)
								: NumberUtils.FormatFloat(fee?.feeAmount, 8),
						termSheetFeeType: fee?.termSheetFeeType ?? 'Currency',
						feeFrequency: fee?.feeFrequency ?? 'Annually',
						feeNotes: fee?.feeNotes ?? '',
					}}
					onSubmit={FormikUtils.MutateOnSubmit(notesMutation.mutate)}
				>
					{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid item xs={11}>
								<CerebroLabel id="termSheetFeeGuid">
									Amount: {'  '}
									<CerebroText component="span">
										{values.termSheetFeeType === 'Currency'
											? `$${values.feeAmount}`
											: `${values.feeAmount}%`}
									</CerebroText>
								</CerebroLabel>
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="termSheetFeeType">
									Type:{'  '}
									<CerebroText component="span">{fee?.termSheetFeeType}</CerebroText>
								</CerebroLabel>
							</Grid>

							<Grid item xs={11} sx={{ marginBottom: 2 }}>
								<CerebroLabel id="feeFrequency">
									Frequency:{'  '}
									<CerebroText component="span">
										{fee?.feeFrequency !== undefined
											? TermSheetFeeFrequenciesMap[fee?.feeFrequency]
											: 'undefined'}
									</CerebroText>
								</CerebroLabel>
							</Grid>

							<Grid item xs={12}>
								<CerebroLabel id="feeNotes">Notes</CerebroLabel>
								<Field
									as={CerebroTextarea}
									id="feeNotes"
									name="feeNotes"
									rows="6"
									disabled={isSubmitting}
								/>
							</Grid>

							<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
								Save
							</CerebroPrimaryButton>
							<CerebroSecondaryButton sx={{ marginTop: 6, marginLeft: 1 }} onClick={() => close()}>
								Cancel
							</CerebroSecondaryButton>
						</CerebroForm>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

type NotesFormErrors = FormikErrors<NotesFormValues>;
type NotesFormMutationVariables = FormikUtils.MutateOnSubmitVariables<NotesFormValues>;
interface NotesFormValues {
	termSheetFeeGuid: string | undefined;
	termSheetFeeType: TermSheetFeeTypes;
	feeAmount: string;
	feeFrequency: TermSheetFeeFrequencies;
	feeNotes: string;
}
